import * as React from 'react';
import { cn } from '../../lib';

/**
 * Entrada de porcentaje. Deja escribir libremente (acepta coma o punto decimal) y solo formatea a un
 * decimal (`20,5`) al salir del campo: reformatear en cada tecla impedía escribir «20».
 */
function parsePercentage(raw: string): number | null {
  const normalized = raw.replace('%', '').replace(',', '.').trim();
  if (normalized === '' || normalized === '.') return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatPercentage(value: number): string {
  return value.toFixed(1).replace('.', ',');
}

export interface PercentageInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: number;
  onChange: (value: number) => void;
  error?: boolean;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  description?: string;
  errorMessage?: string;
  min?: number;
  max?: number;
  step?: number;
}

const PercentageInput = React.forwardRef<HTMLInputElement, PercentageInputProps>(
  ({ className, value, onChange, error, disabled, placeholder = '0,0', label, description, errorMessage, min = 0, max = 100, step: _step, onBlur, ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState(() => (value ? formatPercentage(value) : ''));

    React.useEffect(() => {
      // Sincroniza solo si el valor externo difiere de lo escrito (no pisa la edición en curso).
      setDisplayValue((current) => {
        const parsed = parsePercentage(current);
        if ((parsed ?? 0) === value) return current;
        return value ? formatPercentage(value) : '';
      });
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/[^\d.,]/g, '');
      setDisplayValue(raw);
      const parsed = parsePercentage(raw);
      onChange(parsed === null ? 0 : Math.max(min, Math.min(max, parsed)));
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const parsed = parsePercentage(displayValue);
      if (parsed === null) {
        setDisplayValue('');
      } else {
        const clamped = Math.max(min, Math.min(max, parsed));
        setDisplayValue(formatPercentage(clamped));
      }
      onBlur?.(e);
    };

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
        )}
        <div className="relative">
          <input
            ref={ref}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-right tabular-nums pr-8 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive focus-visible:ring-destructive',
              className
            )}
            aria-invalid={error}
            aria-label={label}
            {...props}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/50 pointer-events-none">
            %
          </span>
        </div>
        {description && !errorMessage && (
          <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
        )}
        {errorMessage && (
          <p className="mt-1.5 text-sm text-destructive" role="alert">{errorMessage}</p>
        )}
      </div>
    );
  }
);
PercentageInput.displayName = 'PercentageInput';

export { PercentageInput };
